import { Injectable } from '@angular/core';
import { OrigamiRequestRestParams, OrigamiResponse, ServiceBase } from './base/service.base';
import { Observable } from 'rxjs';
import {
  InvestigativeSiteServiceModel,
  InvestigativeSiteServiceOptions,
  invSiteRetrievalOptions,
} from './investigative-site.service';
import {
  InvestigativeSite,
  InvestigativeSiteSSUOptions,
  SimpleTopic,
  TopicField,
  TopicUtils,
} from '@app/origami-api/utils/topic-utils';
import { SSUEssentialDocument } from '@app/origami-api/services/essential-documents.service';
import {
  EntityType,
  ICommonDataProtocol,
  ICommonMethodOfDataReceipt,
} from '@app/ti-components/study-start-up/bread-crumbs/models/commonDataProtocol';
import { SessionService } from '@app/session/session.service';
import { map } from 'rxjs/operators';
import { FieldNames } from '@app/origami-api/constants/field-names';
import { AmendmentProfileServiceModel } from '@app/origami-api/services/amendment-profile.service';
import { UserServiceOptions } from './user.service';
import { ErrorCode } from '@app/origami-api/constants/error-codes';
import { InvestigativeSite as InvestigativeSiteConstant } from '@app/origami-api/constants/investigative-site';
import GetSSUDocumentsByViewOptions = StudyStartupServiceModels.GetSSUDocumentsByViewOptions;
import GetSSUDocumentsByView = StudyStartupServiceModels.GetSSUDocumentsByView;
import GetCountryStartupInformationOptions = StudyStartupServiceModels.GetCountrySSUInformationOptions;
import GetCountryStartupInformation = StudyStartupServiceModels.GetCountrySSUInformation;
import GetInvSiteSSUInformationOptions = StudyStartupServiceModels.GetInvSiteSSUInformationOptions;
import GetInvSiteSSUInformation = StudyStartupServiceModels.GetInvSiteSSUInformation;
import SaveEntityAmendmentBody = StudyStartupServiceModels.SaveEntityAmendmentBody;
import AmendmentEntity = StudyStartupServiceModels.AmendmentEntity;

export namespace StudyStartupServiceModels {
  import EssentialFor = SSUEssentialDocument.EssentialFor;
  import MissingDocumentsInfo = SSUEssentialDocument.MissingDocumentsInfo;

  export interface AmendmentEntity {
    AccessRight: number;
    AmendmentId: number;
    AmendmentTopicTypeId: number;
    AmendmentStatusId: number;
    CurrentUserCanCompleteAmendment: boolean;
    Progress: number;
    SSUFolderId: number;
    TopicFields: TopicField[];
  }

  export type SSUGetInvestigativeSiteResult = InvestigativeSite;

  export interface GetSSUDocumentsByView {
    Documents: SimpleTopic[];
    IsCurrentUserSSUApprover?: boolean;
    IsCurrentUserStartupSpecialist?: boolean;
    IsEntityFrozen?: boolean;
    TotalRowCount: number;
  }

  export interface GetSSUDocumentsByViewOptions {
    siteId: number;
    essentialFor: EssentialFor;
    essentialForId: string;
    amendmentProfileId?: number;
    viewMode?: GetSSUDocumentsViewMode;
    first?: number;
    limit?: number;
    parentId?: string;
    search?: string;
    searchQuery?: string;
    searchByFieldsInIndex?: boolean;
    ftsQuery?: string;
    ftsQueryDataType?: any;
    orderByClause?: string;
    fieldListing: string;
  }

  export interface GetInvSiteSSUInformationOptions {
    siteId: number;
    investigativeSiteId: number;
    investigativeSiteInDataRoomId: number;
    returnPreCalculatedData: boolean;
  }

  export interface SaveEntityAmendmentBody {
    siteId: number;
    amendment: {
      AmendmentId: number;
      TopicFields: TopicField[];
    };
  }

  export interface GetInvSiteSSUInformation extends InvestigativeSiteSSUOptions {
    EssentialDocumentStatus: EssentialDocumentStatus[];
    ExpectedSubmissionDate: string;
    AgencySubmissionProgressPercent: number;
    IRBApprovalDate: string;
    IRBSubmitDate: string;
    NextPreSubmissionDeadlineDate: string;
    ProjectedContractApprovalDate: string;
    ProjectedIRBApprovalDate: string;
    ProjectedSiteActivationDate: string;
    RegPackSentDate: string;
    SiteActivationDate: string;
    SSUFolderAccessRight: number;
    SSUFolderId: number;
  }

  export interface GetCountrySSUInformationOptions {
    siteId: number;
    countryInDataRoomId: number;
    returnPreCalculatedData: boolean;
  }

  export interface GetCountrySSUInformation {
    AgencySubmissionProgressPercent: number;
    CountryActivationDate: string;
    CurrentUserCanActivateReject: boolean;
    EssentialDocumentStatus: EssentialDocumentStatus[];
    IsCountryFrozen: boolean;
    SSUFolderAccessRight: number;
    SSUFolderId: number;
  }

  export interface EssentialDocumentStatus {
    EntityId: string;
    EntityName: string;
    EntityType: number;
    EntityStatusId?: number;
    MissingDocuments: MissingDocumentsInfo[];
    ProgressPercent: number;
    RegProgressPercent: number;
    SponsorApprovedCount: number;
    SponsorUnderReviewCount: number;
    specificCountryData?: GetCountrySSUInformation;
    IsEntityApproved?: boolean;
  }

  export enum GetSSUDocumentsViewMode {
    DisplayInGridFavorite = 0,
    DisplayInGridRequired = 1,
    DisplayInGridMissing = 2,
    DisplayInGridNonRequired = 3,
    DisplayInGridAllUploaded = 4,
    ReturnAnyDocs = 5,
    MoveToIndex = 6,
    SaveToDb = 7,
    Export = 8,
    MoveToIndexAndSaveToDb = 9,
  }
  export interface IInstitutionalStartupInformation {
    siteId: number;
    irbInDataRoomId: number;
    returnPreCalculatedData: boolean;
  }
  export interface IInstitutionalStartupInformationResponse {
    CurrentUserCanApproveReject: boolean;
    EssentialDocumentStatus: [
      {
        EntityId: string;
        EntityName: string;
        EntityType: number;
        MissingDocuments: [];
        ProgressPercent: number;
        RegProgressPercent: number;
        SponsorApprovedCount: number;
        SponsorUnderReviewCount: number;
      },
    ];
    IRBApprovalDate: string | null;
    IsIRBFrozen: boolean;
    SSUFolderAccessRight: number;
    SSUFolderId: boolean;
  }

  export enum RegulatoryReviewViewMode {
    ByRegApprovalStatus = 1,
    ByInvestigativeSite = 2,
    ByCountry = 3,
    ByIRB = 4,
    ByDocumentType = 5,
  }

  export enum SponsorReviewViewMode {
    All = 82,
    ByInvestigativeSite = 83,
    ByCountry = 84,
    ByIRB = 85,
    ByDocumentType = 86,
  }

  export enum GetSSUDocumentsViewsViewMode {
    ByInvestigativeSite = 1,
    ByCountry = 2,
    ByDocumentType = 3,
    FromInbox = 4,
    ByPostedBy = 5,
    ByIrbEc = 6,
    ByDeletedDocuments = 7,
    ByStaging = 8,
  }

  export interface GetSSUDocumentsViewsOptions {
    siteId: number;
    essentialFor?: number;
    essentialForId?: string | number;
    viewFilter?: any;
    viewMode?: GetSSUDocumentsViewsViewMode;
    amendmentProfileId?: number | string;
    searchTextGetter?: () => string;
  }

  export enum SSUReviewerApprovalMode {
    RegulatoryReview = 2,
    SponsorReview,
  }

  export interface GetRegulatoryReviewViewsOptions {
    siteId: number;
    essentialFor?: number;
    essentialForId?: string | number;
    viewFilter?: any;
    viewMode?: RegulatoryReviewViewMode;
    amendmentProfileId?: number | string;
    approvalMode: SSUReviewerApprovalMode;
    searchTextGetter?: () => string;
  }

  export interface GetSSUDocumentsViews {
    ViewFilterDisplayName?: string;
    ViewFilterId?: string;
    ViewFilterIdAdditional?: string;
    ViewMode?: number;
    EntitiesCount?: number;
  }

  export interface ChangeInvestigativeSiteStatus {
    siteId: number;
    investigativeSiteId: number;
    siteFields: TopicField[];
    status: InvestigativeSiteConstant.Status;
    issues?: any[];
    documentIdsMovingToEtmf: number[];
    doNotMoveAnyDocumentsToEtmf: boolean;
    completeAmendments: boolean;
  }
}

@Injectable()
export class StudyStartupService extends ServiceBase implements ICommonMethodOfDataReceipt {
  serviceUrl = 'StudyStartupService.svc';

  getInstitutionalReviewBoardStartupInformation(
    body: StudyStartupServiceModels.IInstitutionalStartupInformation,
    ...params
  ): Observable<OrigamiResponse<StudyStartupServiceModels.IInstitutionalStartupInformationResponse>> {
    return this.origamiPost('/GetInstitutionalReviewBoardStartupInformation', body, ...params);
  }

  getInvestigativeSites(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSites,
    ...params
  ): Observable<OrigamiResponse<InvestigativeSiteServiceOptions.GetInvestigativeSitesResult>> {
    return this.origamiPost('/GetInvestigativeSites', body, ...params);
  }

  getInvestigativeSite(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSite,
    ...params
  ): Observable<OrigamiResponse<StudyStartupServiceModels.SSUGetInvestigativeSiteResult>> {
    return this.origamiPost('/GetInvestigativeSite', body, ...params);
  }

  public getSSUDocumentsViews(
    body: StudyStartupServiceModels.GetSSUDocumentsViewsOptions,
    options = null,
    silentErrorCodes = null,
    forceRequest = true,
  ): Observable<OrigamiResponse<StudyStartupServiceModels.GetSSUDocumentsViews[]>> {
    return this.origamiPost('/GetSSUDocumentsViews', body, options, silentErrorCodes, forceRequest);
  }

  public getRegulatoryReviewViews(
    body: StudyStartupServiceModels.GetRegulatoryReviewViewsOptions,
    options = null,
    silentErrorCodes = null,
    forceRequest = false,
  ): Observable<OrigamiResponse<StudyStartupServiceModels.GetSSUDocumentsViews[]>> {
    return this.origamiPost('/GetRegulatoryReviewViews', body, options, silentErrorCodes, forceRequest);
  }

  public getSSUDocumentsByView(
    body: GetSSUDocumentsByViewOptions,
    force: boolean,
  ): Observable<OrigamiResponse<GetSSUDocumentsByView>> {
    return this.origamiPost('/GetSSUDocumentsByView', body, null, null, force);
  }

  public getCountryStartupInformation(
    body: GetCountryStartupInformationOptions,
    silentCodes: number[] = null,
    force: boolean = true,
  ): Observable<OrigamiResponse<GetCountryStartupInformation>> {
    return this.origamiPost('/GetCountryStartupInformation', body, null, silentCodes, force);
  }

  public getInvestigativeSiteStartupInformation(
    body: GetInvSiteSSUInformationOptions,
    silentCodes: number[] = null,
    force: boolean = true,
  ): Observable<OrigamiResponse<GetInvSiteSSUInformation>> {
    return this.origamiPost('/GetInvestigativeSiteStartupInformation', body, null, silentCodes, force);
  }

  public getTruncatedInvestigativeSites(body: InvestigativeSiteServiceOptions.GetInvestigativeSites, ...params) {
    return this.origamiPost('/GetTruncatedInvestigativeSites', body, ...params);
  }

  public getEntity(
    searchText: string,
    sessionService: SessionService,
    _,
    limit?: number,
  ): Observable<ICommonDataProtocol[]> {
    let request: InvestigativeSiteServiceOptions.GetInvestigativeSites = {
      siteId: sessionService.currentRoomId,
      domainLevelFields: [
        FieldNames.InvestigativeSite.Address,
        FieldNames.InvestigativeSite.Country,
        FieldNames.InvestigativeSite.State,
      ],
      options: invSiteRetrievalOptions.inDataRoom,
      orderByClause: '[TopicName] ASC',
      siteLevelFields: [
        FieldNames.InvestigativeSite.SiteNumber,
        FieldNames.InvestigativeSite.SiteGroupId,
        FieldNames.InvestigativeSite.ExpirationDate,
        FieldNames.InvestigativeSite.DisplayName,
        FieldNames.InvestigativeSite.GroupMembers,
        FieldNames.InvestigativeSite.Status,
        FieldNames.InvestigativeSite.AutoSiteName,
      ],
      sponsorId: sessionService.room.info.SponsorId,
      tmfPublishing: { RetrieveFromTmf: false },
      useMySitesOnlyFilter: 0,
      start: 0,
      limit: limit ? limit : 20,
      searchClause: searchText ? searchText : '',
    };
    return this.getTruncatedInvestigativeSites(request).pipe(
      map((response) =>
        response.Result.InvSitesList.map((irb) => {
          return {
            name: TopicUtils.getTopicFieldValue(irb.InvestigativeSiteBindedToDataRoom.TopicFields, '$AutoSiteName$'),
            value: irb.InvestigativeSiteBindedToDataRoom.TopicId,
            topicId: irb.InvestigativeSite.TopicId,
            type: EntityType.Sites,
          };
        }),
      ),
    );
  }

  getEntityAmendment(
    body: AmendmentProfileServiceModel.GetEntityAmendmentOptions,
    ...params: OrigamiRequestRestParams
  ): Observable<OrigamiResponse<AmendmentEntity>> {
    return this.origamiPost('/GetEntityAmendment', body, ...params);
  }

  setNotApplicableAmendments(body: AmendmentProfileServiceModel.SetNotApplicableAmendmentsOptions): Observable<any> {
    return this.origamiPost('/SetNotApplicableAmendments', body, null, null, true);
  }
  setNotApplicableAmendmentsStatus(
    body: AmendmentProfileServiceModel.SetNotApplicableAmendmentsStatusOptions,
  ): Observable<any> {
    return this.origamiPost('/SetNotApplicableAmendmentsStatus', body, null, null, true);
  }

  completeAmendment(body: AmendmentProfileServiceModel.CompleteAmendmentOptions): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/CompleteAmendment', body, null, null, true);
  }
  getInvestigativeSiteViews(
    body: InvestigativeSiteServiceOptions.GetInvestigativeSiteViews,
    options = null,
    silentErrorCodes = null,
    forceRequest = true,
  ): Observable<OrigamiResponse<InvestigativeSiteServiceModel.InvestigativeSiteView[]>> {
    return this.origamiPost('/GetInvestigativeSiteViews', body, options, silentErrorCodes, forceRequest);
  }
  saveEntityAmendment(body: SaveEntityAmendmentBody): Observable<any> {
    return this.origamiPost('/SaveEntityAmendment', body, null, null, true);
  }

  sendRegulatoryPacket(
    body: {
      siteId: number;
      invSiteTopicIds: number[];
      sendMailParams: UserServiceOptions.SendMailOptions;
    },
    silentErrorCodes = null,
  ): Observable<any> {
    return this.origamiPost('/SendRegulatoryPacket', body, null, silentErrorCodes, true);
  }

  changeInvestigativeSiteStatus(
    params: StudyStartupServiceModels.ChangeInvestigativeSiteStatus,
    silentErrorCodes?: ErrorCode[],
    forceRequest?: boolean,
  ): Observable<OrigamiResponse<any>> {
    return this.origamiPost('/ChangeInvestigativeSiteStatus', params, null, silentErrorCodes, forceRequest);
  }
}
